import React, { useEffect, useState } from 'react';
import Card from '../Common/Card';
import Button from '../Common/Button';
import { useWeb3 } from '../../context/Web3Context';
import { shortAddress, formatUSDT, copyToClipboard } from '../../utils/format';
import { notify } from '../../utils/notify';

const PAGE_SIZE = 20;

const UserList = ({ onSelect, reloadKey }) => {
  const { contract } = useWeb3();
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    if (!contract) return;
    setLoading(true);
    try {
      const [ids, totalCount] = await contract.getAllUserIds(page, PAGE_SIZE);
      setTotal(Number(totalCount.toString()));

      const data = await Promise.all(
        ids.map(async (id) => {
          try {
            const u = await contract.users(id);
            const isBl = await contract.blacklistedUsers(id);
            return {
              id: id.toString(),
              wallet: u.wallet,
              totalInvestment: u.totalInvestment.toString(),
              activeInvestment: u.activeInvestment.toString(),
              active: u.active,
              blacklisted: isBl
            };
          } catch (e) {
            return null;
          }
        })
      );
      setRows(data.filter(Boolean));
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [contract, page, reloadKey]);

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase().trim();
    return r.id.includes(s) || r.wallet.toLowerCase().includes(s);
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const copyWallet = async (e, addr) => {
    e.stopPropagation();
    const ok = await copyToClipboard(addr);
    if (ok) notify.success('Wallet copied');
  };

  return (
    <Card
      title="All Users"
      subtitle={`${total.toLocaleString()} registered · Click any row to inspect`}
    >
      <div style={{ marginBottom: 16 }}>
        <input
          className="form-input"
          placeholder="Filter current page by ID or wallet"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="table-wrap" style={{ background: 'transparent', border: 'none', overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Wallet</th>
              <th>Total Invested</th>
              <th>Active Stake</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No users on this page</td></tr>
            ) : filtered.map((r) => (
              <tr
                key={r.id}
                onClick={() => onSelect && onSelect(r.id)}
                style={{ cursor: 'pointer' }}
              >
                <td className="numeric" style={{ color: 'var(--accent-cyan)', fontWeight: 600 }}>#{r.id}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="numeric">{shortAddress(r.wallet)}</span>
                    <button
                      onClick={(e) => copyWallet(e, r.wallet)}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--border-secondary)',
                        color: 'var(--accent-cyan)',
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontSize: 10,
                        cursor: 'pointer'
                      }}
                      title="Copy wallet"
                    >⎘</button>
                  </div>
                </td>
                <td className="numeric">{formatUSDT(r.totalInvestment)} USDT</td>
                <td className="numeric">{formatUSDT(r.activeInvestment)} USDT</td>
                <td>
                  {r.blacklisted ? (
                    <span className="badge badge-pink">Blacklisted</span>
                  ) : r.active ? (
                    <span className="badge badge-green"><span className="badge-dot" />Active</span>
                  ) : (
                    <span className="badge badge-muted">Inactive</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap'
      }}>
        <div style={{
          fontFamily: 'Orbitron, sans-serif',
          fontSize: 11,
          letterSpacing: '0.15em',
          color: 'var(--text-muted)',
          textTransform: 'uppercase'
        }}>
          Page {page + 1} of {totalPages}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >‹ Prev</Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages - 1 || loading}
          >Next ›</Button>
        </div>
      </div>
    </Card>
  );
};

export default UserList;
